import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError, themeApi, type Theme, type ThemeListItem } from '../api';
import { useApp } from '../context';
import type { PlexItem } from '../plex';
import { linkProps, navigate } from '../router';
import { Card } from '../components/Card';
import { PlayIcon } from '../components/Icons';
import { applyTheme, chooseTheme, currentTheme, restoreTheme } from '../theme';

/**
 * Skin-studio: een skin samenstellen zonder code. Elke wijziging is meteen in de hele app
 * te zien. Opslaan voor jezelf (dit profiel) of, als eigenaar, voor iedereen.
 * Exporteren en importeren als JSON, hetzelfde formaat dat de Apple-app straks leest.
 */

// ---------- Lettertypes (Google Fonts) ----------

interface FontDef { name: string; axis: string | null }
const FONTS: FontDef[] = [
  { name: 'Systeem', axis: null },
  { name: 'Inter', axis: 'wght@400;500;600;700;800' },
  { name: 'Manrope', axis: 'wght@400;500;600;700;800' },
  { name: 'Outfit', axis: 'wght@400;500;600;700;800' },
  { name: 'Poppins', axis: 'wght@400;500;600;700;800' },
  { name: 'Montserrat', axis: 'wght@400;500;600;700;800' },
  { name: 'Nunito', axis: 'wght@400;500;600;700;800' },
  { name: 'DM Sans', axis: 'wght@400;500;600;700;800' },
  { name: 'Figtree', axis: 'wght@400;500;600;700;800' },
  { name: 'Plus Jakarta Sans', axis: 'wght@400;500;600;700;800' },
  { name: 'Lexend', axis: 'wght@400;500;600;700;800' },
  { name: 'Rubik', axis: 'wght@400;500;600;700;800' },
  { name: 'Sora', axis: 'wght@400;500;600;700;800' },
  { name: 'Urbanist', axis: 'wght@400;500;600;700;800' },
  { name: 'Space Grotesk', axis: 'wght@400;500;600;700' },
  { name: 'Exo 2', axis: 'wght@400;500;600;700;800' },
  { name: 'Roboto', axis: 'wght@400;500;700' },
  { name: 'Oswald', axis: 'wght@400;500;600;700' },
  { name: 'Bebas Neue', axis: null },
  { name: 'Playfair Display', axis: 'wght@400;600;700;800' },
];

function fontCss(name: string): string {
  return name === 'Systeem' ? 'system-ui, sans-serif' : `'${name}', system-ui, sans-serif`;
}

function fontName(css: string | undefined): string {
  const m = css?.match(/^\s*['"]([^'"]+)['"]/);
  return m && FONTS.some((f) => f.name === m[1]) ? m[1] : 'Systeem';
}

function googleQuery(names: string[]): string | undefined {
  const parts = [...new Set(names)]
    .map((n) => FONTS.find((f) => f.name === n))
    .filter((f): f is FontDef => !!f && f.name !== 'Systeem')
    .map((f) => `family=${f.name.replace(/ /g, '+')}${f.axis ? `:${f.axis}` : ''}`);
  return parts.length ? parts.join('&') : undefined;
}

// ---------- Kleuren ----------

function rgb(hex: string): [number, number, number] {
  let h = hex.replace('#', '');
  if (h.length === 3 || h.length === 4) h = h.slice(0, 3).split('').map((x) => x + x).join('');
  const n = parseInt(h.slice(0, 6), 16) || 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function hex([r, g, b]: number[]): string {
  return `#${[r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}

function mix(a: string, b: string, t: number): string {
  const x = rgb(a);
  const y = rgb(b);
  return hex(x.map((v, i) => v + (y[i] - v) * t));
}

function luminance(c: string): number {
  const [r, g, b] = rgb(c).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: string, b: string): number {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
}

function readableOn(bg: string): string {
  return contrast(bg, '#FFFFFF') >= contrast(bg, '#0B0D12') ? '#FFFFFF' : '#0B0D12';
}

/** Uit achtergrond, tekst en accent de overige kleuren afleiden. */
function derive(color: Record<string, string>): Record<string, string> {
  const bg = color.background;
  const text = color.text;
  const accent = color.accent;
  const success = color.success ?? '#6EE7A8';
  return {
    ...color,
    surface: mix(bg, text, 0.05),
    surfaceRaised: mix(bg, text, 0.1),
    border: mix(bg, text, 0.2),
    textMuted: mix(text, bg, 0.28),
    textSubtle: mix(text, bg, 0.45),
    accentText: readableOn(accent),
    focusRing: accent,
    live: color.live ?? '#FF6B57',
    success,
    successSurface: mix(bg, success, 0.16),
  };
}

const COLOR_LABELS: [string, string][] = [
  ['background', 'Achtergrond'],
  ['surface', 'Vlak'],
  ['surfaceRaised', 'Vlak (hoger)'],
  ['border', 'Randen'],
  ['text', 'Tekst'],
  ['textMuted', 'Tekst (zacht)'],
  ['textSubtle', 'Tekst (zachtst)'],
  ['accent', 'Accent'],
  ['accentText', 'Tekst op accent'],
  ['focusRing', 'Focusrand'],
  ['live', 'Live'],
  ['success', 'Gelukt / in bibliotheek'],
  ['successSurface', 'Achter "gelukt"'],
];

const ACCENTS = ['#F5A524', '#E50914', '#FF5C8A', '#A88BFF', '#5B8CFF', '#7CC4FF', '#2FD1C5', '#4ADE80', '#FFFFFF'];

// ---------- Helpers voor de vorm ----------

function clone(t: Theme): Theme {
  return JSON.parse(JSON.stringify(t)) as Theme;
}

function slug(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'skin';
}

type Comp = NonNullable<Theme['components']>;

// ---------- Scherm ----------

export function SkinStudio({ themeId }: { themeId: string | null }) {
  const [draft, setDraft] = useState<Theme | null>(null);
  const [editing, setEditing] = useState<{ id: string; scope: 'personal' | 'shared' } | null>(null);
  const [list, setList] = useState<ThemeListItem[]>([]);
  const [canShare, setCanShare] = useState(false);
  const [msg, setMsg] = useState<{ text: string; error?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAllColors, setShowAllColors] = useState(false);

  // Startpunt laden: een bestaande skin bewerken, een ingebouwde kopiëren, of de huidige als basis
  useEffect(() => {
    themeApi.list().then((r) => { setList(r.themes); setCanShare(r.canShare); }).catch(() => undefined);
    (async () => {
      const base = themeId ? await themeApi.get(themeId) : currentTheme() ?? (await themeApi.get('tumedia'));
      const t = clone(base);
      if (themeId && (base.scope === 'personal' || base.scope === 'shared')) {
        setEditing({ id: base.id, scope: base.scope });
      } else {
        t.name = `${base.name.replace(/\s*\(.*\)$/, '')} (eigen)`;
      }
      setDraft(t);
    })().catch(() => setMsg({ text: 'Skin laden mislukt.', error: true }));
    // Bij het verlaten de gekozen skin terugzetten (na opslaan is dat de nieuwe versie)
    return () => {
      void restoreTheme();
    };
  }, [themeId]);

  // Elke wijziging direct in de hele app tonen
  useEffect(() => {
    if (!draft) return;
    const t = window.setTimeout(() => applyTheme(draft), 40);
    return () => window.clearTimeout(t);
  }, [draft]);

  const set = (fn: (t: Theme) => void) => setDraft((d) => {
    if (!d) return d;
    const n = clone(d);
    fn(n);
    return n;
  });
  const setColor = (k: string, v: string) => set((t) => { t.tokens.color[k] = v; });
  const setComp = <K extends keyof Comp>(k: K, v: Comp[K]) => set((t) => { t.components = { ...t.components, [k]: v }; });

  const warnings = useMemo(() => {
    if (!draft) return [];
    const c = draft.tokens.color;
    const out: string[] = [];
    if (c.text && c.background && contrast(c.text, c.background) < 4.5) out.push('Tekst is slecht leesbaar op de achtergrond.');
    if (c.textMuted && c.background && contrast(c.textMuted, c.background) < 3) out.push('Zachte tekst is erg zwak op de achtergrond.');
    if (c.accent && c.accentText && contrast(c.accent, c.accentText) < 4.5) out.push('Tekst op het accent (knoppen) is slecht leesbaar.');
    if (c.accent && c.background && contrast(c.accent, c.background) < 2) out.push('Het accent valt weg tegen de achtergrond: de focus is dan lastig te zien op tv.');
    return out;
  }, [draft]);

  const save = async (scope: 'personal' | 'shared') => {
    if (!draft) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await themeApi.save(draft, scope, editing?.scope === scope ? editing.id : undefined);
      await chooseTheme(r.theme);
      setEditing({ id: r.theme.id, scope });
      setMsg({ text: scope === 'shared' ? 'Opgeslagen voor iedereen en in gebruik genomen.' : 'Opgeslagen en in gebruik genomen.' });
      themeApi.list().then((x) => setList(x.themes)).catch(() => undefined);
    } catch (err) {
      setMsg({ text: err instanceof ApiError && err.message !== err.code ? err.message : 'Opslaan mislukt.', error: true });
    } finally {
      setBusy(false);
    }
  };

  const loadBase = async (id: string) => {
    const t = await themeApi.get(id).catch(() => null);
    if (!t || !draft) return;
    const n = clone(t);
    n.name = draft.name;
    setDraft(n);
    setMsg({ text: `Kleuren en vormen van "${t.name}" overgenomen.` });
  };

  const exportJson = () => {
    if (!draft) return;
    const { scope: _scope, ...rest } = draft;
    void _scope;
    const out = { ...rest, id: slug(draft.name), $schema: 'https://github.com/AndyPanerai/tumedia/blob/main/packages/themes/theme.schema.json' };
    const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${slug(draft.name)}.json`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };

  const importJson = (text: string) => {
    try {
      const t = JSON.parse(text) as Theme;
      if (!t?.tokens?.color?.background || !t.tokens.color.text || !t.tokens.color.accent) throw new Error('Geen geldige skin: kleuren ontbreken.');
      t.tokens.font ??= {};
      t.tokens.radius ??= { card: 12, button: 12, pill: 999 };
      t.layout ??= { nav: 'top', home: [] };
      t.components ??= {};
      setDraft({ ...t, name: t.name || 'Geïmporteerde skin' });
      setEditing(null);
      setMsg({ text: 'Skin geïmporteerd. Pas aan en sla op.' });
    } catch (err) {
      setMsg({ text: err instanceof Error ? err.message : 'Importeren mislukt.', error: true });
    }
  };

  const remove = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await themeApi.remove(editing.id);
      navigate('/settings/look');
    } catch {
      setMsg({ text: 'Verwijderen mislukt.', error: true });
      setBusy(false);
    }
  };

  if (!draft) {
    return <main className="page"><header className="page-head"><h1>Skin-studio</h1></header>{msg ? <p className="placeholder-row error">{msg.text}</p> : <p className="placeholder-row">Laden…</p>}</main>;
  }

  const c = draft.tokens.color;
  const comp = draft.components ?? {};
  const displayFont = fontName(draft.tokens.font.display);
  const bodyFont = fontName(draft.tokens.font.body);
  const setFonts = (display: string, body: string) => set((t) => {
    t.tokens.font = { display: fontCss(display), body: fontCss(body), googleFonts: googleQuery([display, body]) };
  });

  return (
    <main className="page studio">
      <header className="page-head editor-head">
        <h1>Skin-studio</h1>
        <div className="spacer" />
        <a className="btn btn-ghost" {...linkProps('/settings/look')}>Sluiten</a>
      </header>
      {msg && <p className={`placeholder-row ${msg.error ? 'error' : ''}`} role="status">{msg.text}</p>}

      <div className="studio-cols">
        <div className="studio-panel">
          <Section title="Naam en startpunt">
            <label className="ed-field">Naam
              <input className="text-input" value={draft.name} maxLength={40} onChange={(e) => { const v = e.target.value; set((t) => { t.name = v; }); }} />
            </label>
            <label className="ed-field">Overnemen van
              <select value="" onChange={(e) => void loadBase(e.target.value)}>
                <option value="">Kies een skin…</option>
                {list.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
          </Section>

          <Section title="Kleuren">
            <div className="ed-field">
              <span>Accent</span>
              <div className="studio-swatches" role="radiogroup" aria-label="Accentkleur">
                {ACCENTS.map((a) => (
                  <button
                    key={a}
                    role="radio"
                    aria-checked={c.accent?.toUpperCase() === a}
                    aria-label={a}
                    className="studio-swatch"
                    style={{ background: a }}
                    onClick={() => set((t) => { t.tokens.color.accent = a; t.tokens.color.accentText = readableOn(a); t.tokens.color.focusRing = a; })}
                  />
                ))}
              </div>
            </div>
            <ColorField label="Achtergrond" value={c.background} onChange={(v) => setColor('background', v)} />
            <ColorField label="Tekst" value={c.text} onChange={(v) => setColor('text', v)} />
            <ColorField label="Accent" value={c.accent} onChange={(v) => setColor('accent', v)} />
            <button className="btn btn-sm" onClick={() => set((t) => { t.tokens.color = derive(t.tokens.color); })}>
              Overige kleuren hierop afstemmen
            </button>
            <button className="chip studio-more" aria-expanded={showAllColors} onClick={() => setShowAllColors((v) => !v)}>
              {showAllColors ? 'Minder kleuren' : 'Alle kleuren'}
            </button>
            {showAllColors && (
              <div className="studio-colors">
                {COLOR_LABELS.map(([k, label]) => <ColorField key={k} label={label} value={c[k] ?? '#000000'} onChange={(v) => setColor(k, v)} />)}
              </div>
            )}
            {warnings.length > 0 && (
              <ul className="studio-warn" aria-live="polite">
                {warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            )}
          </Section>

          <Section title="Lettertype en grootte">
            <label className="ed-field">Koppen
              <select value={displayFont} onChange={(e) => setFonts(e.target.value, bodyFont)}>
                {FONTS.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
              </select>
            </label>
            <label className="ed-field">Tekst
              <select value={bodyFont} onChange={(e) => setFonts(displayFont, e.target.value)}>
                {FONTS.filter((f) => f.name !== 'Bebas Neue').map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
              </select>
            </label>
            <Slider label="Grootte van alles" value={draft.tokens.scale ?? 1} min={0.8} max={1.3} step={0.05} fmt={(v) => `${Math.round(v * 100)}%`} onChange={(v) => set((t) => { t.tokens.scale = v; })} />
          </Section>

          <Section title="Vormen en focus">
            <Slider label="Hoeken van tegels" value={draft.tokens.radius.card ?? 12} min={0} max={28} step={1} fmt={(v) => `${v}px`} onChange={(v) => set((t) => { t.tokens.radius.card = v; })} />
            <Slider label="Hoeken van knoppen" value={draft.tokens.radius.button ?? 12} min={0} max={28} step={1} fmt={(v) => `${v}px`} onChange={(v) => set((t) => { t.tokens.radius.button = v; })} />
            <Choice label="Focus" value={draft.tokens.focus?.style ?? 'ring'} options={[['ring', 'Rand'], ['glow', 'Gloed'], ['lift', 'Optillen']]} onChange={(v) => set((t) => { t.tokens.focus = { ...t.tokens.focus, style: v }; })} />
            <Slider label="Vergroten bij focus" value={draft.tokens.focus?.scale ?? 1.04} min={1} max={1.12} step={0.01} fmt={(v) => `${Math.round((v - 1) * 100)}%`} onChange={(v) => set((t) => { t.tokens.focus = { ...t.tokens.focus, scale: v }; })} />
          </Section>

          <Section title="Menu">
            <Choice label="Plaats" value={draft.layout.nav === 'left' ? 'left' : 'top'} options={[['top', 'Boven'], ['left', 'Links (Fuse)']]} onChange={(v) => set((t) => { t.layout = { ...t.layout, nav: v }; })} />
            <Choice label="Actief item" value={comp.navStyle ?? 'pill'} options={[['pill', 'Pil'], ['underline', 'Streep'], ['plain', 'Vet']]} onChange={(v) => setComp('navStyle', v)} />
            <Toggle label="Glas: doorzichtig, wazig menu" checked={!!comp.glass} onChange={(v) => setComp('glass', v)} />
            <Toggle label="Klok in het menu" checked={!!comp.clock} onChange={(v) => setComp('clock', v)} />
            <Toggle label="Draaiende disc op infopagina's" checked={comp.disc !== false} onChange={(v) => setComp('disc', v)} />
            <Choice label="Afleveringen bij series" value={comp.episodeView ?? 'list'} options={[['list', 'Lijst (onder elkaar)'], ['cards', 'Brede kaarten (rij)'], ['cinema', 'Bioscoop (alles op de achtergrond)']]} onChange={(v) => setComp('episodeView', v)} />
            <Toggle label="Seizoensthema (sneeuw, vuurwerk…)" checked={comp.seasonal !== 'off'} onChange={(v) => setComp('seasonal', v ? 'auto' : 'off')} />
          </Section>

          <Section title="Titelpagina">
            <PicChoice
              label="Indeling"
              value={comp.titleLayout ?? 'classic'}
              options={[
                ['classic', 'Klassiek', 'Achtergrond bovenaan, de rest eronder'],
                ['cinematic', 'Cinematic', 'Achtergrond over de hele pagina, knoppen van glas'],
              ]}
              onChange={(v) => setComp('titleLayout', v)}
            />
            <p className="subtle hint">Kijkers kunnen op de titelpagina zelf ook wisselen met de knop naast de andere knoppen.</p>
            <Toggle label="Kleur volgt de titel (achtergrond en gloed kleuren mee met de film)" checked={!!comp.followColor} onChange={(v) => setComp('followColor', v)} />
            <Toggle label="Minder beweging (geen zoom of animaties; voor oudere apparaten)" checked={!!comp.reduceMotion} onChange={(v) => setComp('reduceMotion', v)} />
          </Section>

          <Section title="Tegels">
            <Choice label="Grootte" value={comp.density ?? 'normal'} options={[['compact', 'Klein'], ['normal', 'Normaal'], ['large', 'Groot']]} onChange={(v) => setComp('density', v)} />
            <Toggle label="Titel onder tegels" checked={comp.cardTitles !== 'hide'} onChange={(v) => setComp('cardTitles', v ? 'show' : 'hide')} />
            <Toggle label="Labels op tegels (in bibliotheek, aanvragen, gezien)" checked={comp.posterBadges !== 'never'} onChange={(v) => setComp('posterBadges', v ? 'auto' : 'never')} />
            <p className="subtle hint">Labels voor kwaliteit, geluid en score (4K, DV, Atmos, ★) stel je in bij <a {...linkProps('/settings/artwork')}>Instellingen → Metadata en artwork</a>.</p>
          </Section>

          <Section title="Spotlight en achtergrond">
            <Choice label="Hoogte spotlight" value={comp.heroHeight ?? 'normal'} options={[['compact', 'Laag'], ['normal', 'Normaal'], ['tall', 'Hoog']]} onChange={(v) => setComp('heroHeight', v)} />
            <Toggle label="Achtergrond wisselt mee met de geselecteerde titel" checked={comp.backdrop === 'focus'} onChange={(v) => setComp('backdrop', v ? 'focus' : 'none')} />
            <Toggle label="Logo's in plaats van titels (clearlogo)" checked={comp.logos !== false} onChange={(v) => setComp('logos', v)} />
          </Section>

          <Section title="Opslaan en delen">
            <div className="studio-actions">
              <button className="btn btn-primary" disabled={busy} onClick={() => void save('personal')}>
                {editing?.scope === 'personal' ? 'Wijzigingen opslaan' : 'Opslaan voor mij'}
              </button>
              {canShare && (
                <button className="btn" disabled={busy} onClick={() => void save('shared')}>
                  {editing?.scope === 'shared' ? 'Opslaan voor iedereen (bijwerken)' : 'Opslaan voor iedereen'}
                </button>
              )}
              <button className="btn btn-ghost" onClick={exportJson}>Exporteren</button>
              <ImportButton onText={importJson} />
              {editing && <DeleteButton busy={busy} onConfirm={() => void remove()} />}
            </div>
            <span className="subtle hint">
              "Voor mij" geldt voor dit profiel op elk apparaat. {canShare ? '"Voor iedereen" kunnen alle profielen kiezen.' : ''} Exporteren geeft een JSON-bestand dat je kunt delen of later importeren.
            </span>
          </Section>
        </div>

        <aside className="studio-preview" aria-label="Voorbeeld">
          <Preview />
        </aside>
      </div>
    </main>
  );
}

// ---------- Voorbeeld met echte titels uit je bibliotheek ----------

function Preview() {
  const { plex } = useApp();
  const [items, setItems] = useState<PlexItem[]>([]);
  useEffect(() => {
    plex.recentlyAdded().then((r) => setItems(r.filter((i) => i.type === 'movie' || i.type === 'show').slice(0, 8))).catch(() => setItems([]));
  }, [plex]);
  const first = items[0];
  const art = first ? plex.image(first.art, 1280, 720) : undefined;
  return (
    <div className="studio-sample">
      <div className="studio-hero" style={art ? { backgroundImage: `url("${art}")` } : undefined}>
        <div className="studio-hero-body">
          <span className="eyebrow">Voorbeeld</span>
          <strong className="studio-hero-title">{first?.title ?? 'Titel van een film'}</strong>
          <div className="hero-actions">
            <button className="btn btn-primary" tabIndex={-1}><PlayIcon /> Afspelen</button>
            <button className="btn" tabIndex={-1}>Meer info</button>
          </div>
        </div>
      </div>
      <div className="filterbar studio-chips">
        <span className="chip" aria-pressed="true">Actief</span>
        <span className="chip">Filter</span>
        <span className="badge">4K</span>
        <span className="badge">Dolby Vision</span>
      </div>
      <div className="row-track studio-row">
        {items.slice(1, 7).map((i) => <Card key={i.ratingKey} item={i} kind="poster" />)}
      </div>
      <p className="subtle hint">De hele app toont je wijzigingen al. Ga met Tab of de pijltjes door het voorbeeld om de focus te zien.</p>
    </div>
  );
}

// ---------- Bouwstenen ----------

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="studio-section" aria-label={title}>
      <h2 className="studio-title">{title}</h2>
      {children}
    </section>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const full = /^#[0-9a-f]{6}$/i.test(value) ? value : /^#[0-9a-f]{3}$/i.test(value) ? `#${value.slice(1).split('').map((x) => x + x).join('')}` : '#000000';
  return (
    <label className="studio-color">
      <span>{label}</span>
      <input type="color" value={full} onChange={(e) => onChange(e.target.value.toUpperCase())} aria-label={`${label} kiezen`} />
      <input
        className="text-input studio-hex"
        value={text}
        maxLength={9}
        spellCheck={false}
        onChange={(e) => {
          setText(e.target.value);
          if (/^#[0-9a-f]{6}$/i.test(e.target.value)) onChange(e.target.value.toUpperCase());
        }}
        aria-label={`${label} als hex-code`}
      />
    </label>
  );
}

function Slider({ label, value, min, max, step, fmt, onChange }: { label: string; value: number; min: number; max: number; step: number; fmt: (v: number) => string; onChange: (v: number) => void }) {
  return (
    <label className="ed-field studio-slider">
      <span>{label} <span className="subtle">{fmt(value)}</span></span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </label>
  );
}

/** Keuze met een plaatje per optie (zoals de looks in DexHub). */
function PicChoice<T extends 'classic' | 'cinematic'>({ label, value, options, onChange }: { label: string; value: T; options: [T, string, string][]; onChange: (v: T) => void }) {
  return (
    <div className="pic-choice" role="radiogroup" aria-label={label}>
      {options.map(([v, name, hint]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} className={`pic-opt${value === v ? ' is-on' : ''}`} onClick={() => onChange(v)}>
          <span className={`pic-thumb pic-${v}`} aria-hidden="true">
            <i className="p-art" /><i className="p-scrim" /><i className="p-l1" /><i className="p-l2" /><i className="p-l3" /><i className="p-btn" /><i className="p-row" />
          </span>
          <span className="pic-name">{name}</span>
          <span className="pic-hint">{hint}</span>
        </button>
      ))}
    </div>
  );
}

function Choice<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="ed-field">
      <span>{label}</span>
      <div className="chip-wrap" role="radiogroup" aria-label={label}>
        {options.map(([id, text]) => (
          <button key={id} role="radio" aria-checked={value === id} aria-pressed={value === id} className="chip" onClick={() => onChange(id)}>{text}</button>
        ))}
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="studio-toggle">
      <span>{label}</span>
      <span className="toggle">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="visually-hidden">{label}</span>
      </span>
    </label>
  );
}

function ImportButton({ onText }: { onText: (text: string) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <button className="btn btn-ghost" onClick={() => input.current?.click()}>Importeren</button>
      <input
        ref={input}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void f.text().then(onText);
          e.target.value = '';
        }}
      />
    </>
  );
}

function DeleteButton({ busy, onConfirm }: { busy: boolean; onConfirm: () => void }) {
  const [sure, setSure] = useState(false);
  return sure ? (
    <span className="studio-confirm">
      <button className="btn btn-danger" disabled={busy} onClick={onConfirm}>Ja, verwijderen</button>
      <button className="btn btn-ghost" onClick={() => setSure(false)}>Nee</button>
    </span>
  ) : (
    <button className="btn btn-ghost" onClick={() => setSure(true)}>Verwijderen</button>
  );
}
